import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Save, Download, CheckCircle, Send, Sparkles,
  FileText, ArrowLeft, RefreshCw, Copy, MessageSquare,
  Loader, X, AlertTriangle, TrendingUp
} from 'lucide-react';
import { pricingIntelligenceAPI } from '../../services/api';
import './ResponseWriting.css';

const ResponseWriting = ({
  tender,
  analysisResults,
  analystConfig,
  response,
  onResponseChange,
  onComplete,
  onBack,
  onRerunAnalysis
}) => {
  const [reportContent, setReportContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showCopied, setShowCopied] = useState(false);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Generate initial report on mount or when analysisResults change
  useEffect(() => {
    const savedReport = loadSavedReport();

    // Check if we should use cached report or regenerate
    // Compare jobId to detect if this is a new analysis
    const currentJobId = analysisResults?.jobId;
    const cachedJobId = savedReport?.analysisResults?.jobId;
    const regenerateAt = analysisResults?._regenerateAt;
    const cachedRegenerateAt = savedReport?.analysisResults?._regenerateAt;

    // Force regenerate if:
    // 1. No saved report
    // 2. Different job ID
    // 3. New regeneration timestamp (indicates fresh analysis just completed)
    const shouldRegenerate = !savedReport ||
      !currentJobId ||
      !cachedJobId ||
      currentJobId !== cachedJobId ||
      (regenerateAt && regenerateAt !== cachedRegenerateAt);

    if (shouldRegenerate) {
      console.log('[ResponseWriting] Generating new report. Current job:', currentJobId, 'Cached job:', cachedJobId, 'Regenerate:', regenerateAt);
      generateAnalystReport();
    } else {
      // Same analysis - use cached report
      console.log('[ResponseWriting] Using cached report for job:', currentJobId);
      setReportContent(savedReport.reportContent || '');
      setChatMessages(savedReport.chatHistory || []);
      setLastSaved(savedReport.lastSaved ? new Date(savedReport.lastSaved) : null);
    }
  }, [analysisResults?.jobId, analysisResults?._regenerateAt]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Auto-save
  useEffect(() => {
    if (reportContent) {
      const timer = setTimeout(() => {
        saveReport();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [reportContent, chatMessages]);

  const loadSavedReport = () => {
    try {
      const tenderId = tender?.tender_id || tender?.id;
      const saved = localStorage.getItem(`bid_response_${tenderId}`);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  };

  const saveReport = () => {
    try {
      const tenderId = tender?.tender_id || tender?.id;
      const data = {
        reportContent,
        chatHistory: chatMessages,
        tenderTitle: tender?.title,
        organization: tender?.organization?.name || tender?.organization?.contracting_entity,
        tender: tender,
        analysisResults: analysisResults,
        analystConfig: analystConfig,
        status: 'draft',
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem(`bid_response_${tenderId}`, JSON.stringify(data));
      localStorage.setItem(`bid_response_${tenderId}_timestamp`, new Date().toISOString());
      setLastSaved(new Date());
    } catch (e) {
      console.error('Error saving report:', e);
    }
  };

  const generateAnalystReport = async () => {
    setIsGenerating(true);

    try {
      const report = buildAnalystReport(tender, analysisResults, analystConfig);
      setReportContent(report);

      // Add initial system message
      setChatMessages([{
        role: 'assistant',
        content: "I've generated your analyst bid report based on the 5-phase sourcing analysis. You can ask me to:\n\n" +
          "* **Update pricing assumptions** - \"Change the exchange rate to 1.40\"\n" +
          "* **Recalculate margins** - \"What if we target 20% margin?\"\n" +
          "* **Modify compliance status** - \"Mark ISO 9001 as compliant\"\n" +
          "* **Add custom notes** - \"Add a note about shipping delays\"\n" +
          "* **Generate executive summary** - \"Make the summary more detailed\""
      }]);
    } catch (error) {
      console.error('Error generating report:', error);
      setReportContent('Error generating report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const formatCurrency = (amount, currency = 'CAD') => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const buildAnalystReport = (tender, analysis, config) => {
    const org = tender?.organization?.name || tender?.organization?.contracting_entity || 'Government of Canada';
    const title = tender?.title || 'Untitled Tender';
    const tenderId = tender?.tender_id || tender?.id || 'N/A';
    const category = tender?.['masterCategory-catégoriePrincipale'] || tender?.procurement_category || 'Goods/Supplies';
    const estimatedValue = tender?.amount || tender?.value?.amount || 100000;

    // Extract phase results (new structure)
    const analysisConfig = analysis?.config || {};
    const phase1 = analysis?.phase1 || {}; // Technical extraction
    const phase2 = analysis?.phase2 || {}; // Canada pricing
    const phase3 = analysis?.phase3 || {}; // OEM/China pricing
    const phase4 = analysis?.phase4 || {}; // Historical contracts
    const phase5 = analysis?.phase5 || {}; // Bid pricing
    const dataQuality = analysis?.dataQuality || {};

    let report = '';

    // ==========================================
    // HEADER
    // ==========================================
    report += `# Bid Analysis Report\n\n`;
    report += `**Tender:** ${title}\n`;
    report += `**ID:** ${tenderId}\n`;
    report += `**Organization:** ${org}\n`;
    report += `**Category:** ${category}\n`;
    report += `**Estimated Value:** ${formatCurrency(estimatedValue)}\n`;
    report += `**Analysis Date:** ${new Date().toLocaleDateString()}\n\n`;

    // ==========================================
    // EXECUTIVE SUMMARY (10 bullets max)
    // ==========================================
    report += `## Executive Summary\n\n`;

    // Bid Recommendation Badge
    const bidRec = phase5?.totalBidSummary?.bidRecommendation || 'REVIEW';
    const confidence = phase5?.totalBidSummary?.confidenceLevel || 'low';
    const winPrice = phase5?.totalBidSummary?.totalWinPrice;
    const balancedPrice = phase5?.totalBidSummary?.totalBalancedPrice;
    const winMargin = phase5?.totalBidSummary?.overallMarginWin;
    const balancedMargin = phase5?.totalBidSummary?.overallMarginBalanced;

    if (bidRec === 'BID') {
      report += `**Recommendation: ✅ BID** (${confidence} confidence)\n\n`;
    } else if (bidRec === 'CAUTION') {
      report += `**Recommendation: ⚠️ CAUTION** (${confidence} confidence)\n\n`;
    } else {
      report += `**Recommendation: ❌ NO BID** (${confidence} confidence)\n\n`;
    }

    // Executive bullets from Phase 5
    const execSummary = phase5?.executiveSummary || [];
    if (execSummary.length > 0) {
      execSummary.forEach(bullet => {
        report += `- ${bullet}\n`;
      });
      report += `\n`;
    } else {
      report += `- Products extracted: ${dataQuality.extractedProducts || 0}\n`;
      report += `- Canada pricing data: ${dataQuality.hasCanadaPricing ? 'Found' : 'Not found'}\n`;
      report += `- China/OEM pricing: ${dataQuality.hasChinaPricing ? 'Found' : 'Not found'}\n`;
      report += `- Historical contracts: ${phase4?.contractsFound || 0} found\n`;
      report += `\n`;
    }

    // Key Numbers
    if (winPrice || balancedPrice) {
      report += `### Recommended Bid Prices\n\n`;
      report += `| Strategy | Bid Price | Margin |\n`;
      report += `|----------|-----------|--------|\n`;
      if (winPrice) report += `| **Win Price** (aggressive) | ${formatCurrency(winPrice)} | ${winMargin} |\n`;
      if (balancedPrice) report += `| **Balanced Price** (protected) | ${formatCurrency(balancedPrice)} | ${balancedMargin} |\n`;
      report += `\n`;
    }

    report += `---\n\n`;

    // ==========================================
    // ANNEX A: COMPLIANCE MATRIX (Phase 1)
    // ==========================================
    report += `## Annex A: Compliance Matrix\n\n`;

    const items = phase1?.items || [];
    if (items.length > 0) {
      report += `| # | Product | Mandatory Specs | Certifications | Risk |\n`;
      report += `|---|---------|-----------------|----------------|------|\n`;
      items.forEach(item => {
        const mandatorySpecs = (item.mandatorySpecs || []).slice(0, 2).join('; ') || '-';
        const certs = (item.certifications || []).join(', ') || '-';
        const risk = item.complianceRisk === 'green' ? '🟢' : item.complianceRisk === 'amber' ? '🟡' : item.complianceRisk === 'red' ? '🔴' : '⚪';
        report += `| ${item.lineItem} | ${(item.productName || item.description || '').substring(0, 30)} | ${mandatorySpecs.substring(0, 30)} | ${certs} | ${risk} |\n`;
      });
      report += `\n`;

      // Detailed product list
      report += `### Extracted Products Detail\n\n`;
      items.forEach(item => {
        report += `**${item.lineItem}. ${item.productName || item.description || 'Unknown'}**\n`;
        if (item.mandatorySpecs?.length > 0) {
          report += `- Mandatory: ${item.mandatorySpecs.join(', ')}\n`;
        }
        if (item.optionalSpecs?.length > 0) {
          report += `- Optional: ${item.optionalSpecs.join(', ')}\n`;
        }
        if (item.certifications?.length > 0) {
          report += `- Certifications: ${item.certifications.join(', ')}\n`;
        }
        if (item.warrantyRequired) {
          report += `- Warranty: ${item.warrantyRequired}\n`;
        }
        report += `- Quantity: ${item.quantity || 'TBD'} ${item.unit || 'EA'}\n`;
        if (item.riskNotes) {
          report += `- ⚠️ ${item.riskNotes}\n`;
        }
        report += `\n`;
      });
    } else {
      report += `*No products extracted. Upload RFP documents for detailed extraction.*\n\n`;
    }

    // Show commodity codes
    if (phase1?.gsinCode || phase1?.unspscCode) {
      report += `### Commodity Codes\n\n`;
      report += `| Code Type | Value |\n`;
      report += `|-----------|-------|\n`;
      if (phase1.gsinCode) report += `| GSIN | ${phase1.gsinCode} |\n`;
      if (phase1.unspscCode) report += `| UNSPSC | ${phase1.unspscCode} |\n`;
      report += `\n`;
    }

    // Delivery info
    if (phase1?.deliveryLocations?.length > 0 || phase1?.deliveryTimeline) {
      report += `### Delivery Requirements\n\n`;
      if (phase1.deliveryLocations?.length > 0) report += `- **Locations:** ${phase1.deliveryLocations.join(', ')}\n`;
      if (phase1.deliveryTimeline) report += `- **Timeline:** ${phase1.deliveryTimeline}\n`;
      if (phase1.canadianContentRequired) report += `- **Canadian Content:** Required\n`;
      if (phase1.securityClearanceRequired) report += `- **Security Clearance:** ${phase1.securityClearanceRequired}\n`;
      report += `\n`;
    }

    report += `---\n\n`;

    // ==========================================
    // CANADA PRICING MAP (Phase 2)
    // ==========================================
    report += `## Canada Pricing Map\n\n`;

    const canadaProducts = phase2?.products || [];
    if (canadaProducts.length > 0) {
      report += `### Retail & Wholesale Price Anchors\n\n`;
      report += `| Product | Retail Anchor | Wholesale Signal | Price Spread |\n`;
      report += `|---------|---------------|------------------|---------------|\n`;
      canadaProducts.forEach(p => {
        const retail = p.retailAnchor?.price ? `${formatCurrency(p.retailAnchor.price)} (${p.retailAnchor.seller || 'Unknown'})` : 'Not found';
        const wholesale = p.wholesaleSignal?.price ? `${formatCurrency(p.wholesaleSignal.price)} (${p.wholesaleSignal.seller || 'Unknown'})` : 'Not found';
        const spread = p.priceSpread ? `${formatCurrency(p.priceSpread.lowRetail)} - ${formatCurrency(p.priceSpread.highRetail)}` : '-';
        report += `| ${(p.name || '').substring(0, 25)} | ${retail} | ${wholesale} | ${spread} |\n`;
      });
      report += `\n`;

      // Detailed pricing
      report += `### Detailed Canadian Pricing\n\n`;
      canadaProducts.forEach((p, idx) => {
        report += `**${idx + 1}. ${p.name || 'Unknown Product'}**\n`;
        if (p.retailAnchor) {
          report += `- Retail: ${formatCurrency(p.retailAnchor.price)} from ${p.retailAnchor.seller || 'Unknown'}\n`;
          if (p.retailAnchor.source) report += `  - Source: ${p.retailAnchor.source}\n`;
          if (p.retailAnchor.notes) report += `  - Notes: ${p.retailAnchor.notes}\n`;
        }
        if (p.wholesaleSignal) {
          report += `- Wholesale: ${formatCurrency(p.wholesaleSignal.price)} from ${p.wholesaleSignal.seller || 'Unknown'}`;
          if (p.wholesaleSignal.minQty) report += ` (min qty: ${p.wholesaleSignal.minQty})`;
          report += `\n`;
        }
        report += `\n`;
      });
    } else {
      report += `*No Canadian pricing data retrieved. AI analysis may have been unable to find comparable products.*\n\n`;
    }

    if (phase2?.marketNotes) {
      report += `**Market Notes:** ${phase2.marketNotes}\n\n`;
    }

    report += `---\n\n`;

    // ==========================================
    // OEM + LANDED COST TABLE (Phase 3)
    // ==========================================
    report += `## OEM Sourcing & Landed Cost Analysis\n\n`;

    const analysisConfigData = analysis?.config || {};
    report += `### Configuration\n\n`;
    report += `| Parameter | Value |\n`;
    report += `|-----------|-------|\n`;
    report += `| Exchange Rate | 1 USD = ${analysisConfigData.exchangeRate || 1.38} CAD |\n`;
    report += `| Shipping | ${analysisConfigData.shippingMode || 'Sea Freight'} from ${analysisConfigData.shippingOrigin || 'China'} |\n`;
    report += `| Duty Rate | ${((analysisConfigData.dutyRate || 0.05) * 100).toFixed(0)}% |\n`;
    report += `| Brokerage | ${formatCurrency(analysisConfigData.brokerageFlat || 150)} flat |\n`;
    report += `| Defect Buffer | ${((analysisConfigData.defectAllowance || 0.02) * 100).toFixed(0)}% |\n`;
    report += `| Warranty Buffer | ${((analysisConfigData.warrantyAllowance || 0.02) * 100).toFixed(0)}% |\n`;
    report += `\n`;

    const oemProducts = phase3?.products || [];
    if (oemProducts.length > 0) {
      report += `### OEM Pricing & Landed Cost\n\n`;
      report += `| Product | FOB (USD) | MOQ | Landed (CAD) | Margin vs Retail |\n`;
      report += `|---------|-----------|-----|--------------|------------------|\n`;
      oemProducts.forEach(p => {
        const fob = p.oemSource?.unitPriceUSD ? `$${p.oemSource.unitPriceUSD.toFixed(2)}` : '-';
        const moq = p.oemSource?.moq || '-';
        const landed = p.landedCostBreakdown?.totalLandedCAD ? formatCurrency(p.landedCostBreakdown.totalLandedCAD) : '-';
        const margin = p.marginVsCanadaRetail || '-';
        report += `| ${(p.name || '').substring(0, 25)} | ${fob} | ${moq} | ${landed} | ${margin} |\n`;
      });
      report += `\n`;

      // Detailed landed cost breakdown for first product
      const firstProduct = oemProducts[0];
      if (firstProduct?.landedCostBreakdown) {
        const lc = firstProduct.landedCostBreakdown;
        report += `### Sample Landed Cost Breakdown: ${firstProduct.name || 'Product 1'}\n\n`;
        report += `| Cost Component | Amount (CAD) |\n`;
        report += `|----------------|--------------|\n`;
        if (lc.fobCAD) report += `| FOB Price | ${formatCurrency(lc.fobCAD)} |\n`;
        if (lc.freightPerUnit) report += `| Freight | ${formatCurrency(lc.freightPerUnit)} |\n`;
        if (lc.dutyAmount) report += `| Duty | ${formatCurrency(lc.dutyAmount)} |\n`;
        if (lc.brokeragePerUnit) report += `| Brokerage | ${formatCurrency(lc.brokeragePerUnit)} |\n`;
        if (lc.insurancePerUnit) report += `| Insurance | ${formatCurrency(lc.insurancePerUnit)} |\n`;
        if (lc.defectBuffer) report += `| Defect Buffer | ${formatCurrency(lc.defectBuffer)} |\n`;
        if (lc.warrantyBuffer) report += `| Warranty Buffer | ${formatCurrency(lc.warrantyBuffer)} |\n`;
        report += `| **TOTAL LANDED** | **${formatCurrency(lc.totalLandedCAD)}** |\n`;
        report += `\n`;
      }

      // Detailed OEM sources
      report += `### OEM Source Details\n\n`;
      oemProducts.forEach((p, idx) => {
        if (p.oemSource) {
          report += `**${idx + 1}. ${p.name || 'Unknown'}**\n`;
          report += `- Supplier: ${p.oemSource.supplier || 'Unknown'}\n`;
          report += `- Price: $${p.oemSource.unitPriceUSD?.toFixed(2) || 'N/A'} USD\n`;
          report += `- MOQ: ${p.oemSource.moq || 'N/A'}\n`;
          if (p.oemSource.leadTime) report += `- Lead Time: ${p.oemSource.leadTime}\n`;
          if (p.oemSource.specs) report += `- Specs: ${p.oemSource.specs}\n`;
          report += `\n`;
        }
      });
    } else {
      report += `*No OEM pricing data retrieved.*\n\n`;
    }

    if (phase3?.sourcingNotes) {
      report += `**Sourcing Notes:** ${phase3.sourcingNotes}\n\n`;
    }

    report += `---\n\n`;

    // ==========================================
    // COMPETITOR PRICE BANDS (Phase 4 - Historical Data)
    // ==========================================
    report += `## Historical Contract Data & Competitor Banding\n\n`;

    const contractsFound = phase4?.contractsFound || 0;
    const similarContracts = phase4?.similarContracts || [];
    const benchmark = phase4?.benchmark;

    if (contractsFound > 0) {
      report += `### Similar Historical Contracts (${contractsFound} found)\n\n`;
      report += `| Contract Title | Value | Supplier |\n`;
      report += `|----------------|-------|----------|\n`;
      similarContracts.slice(0, 10).forEach(c => {
        const contractTitle = (c.title || 'Unknown').substring(0, 40);
        const value = c.value || c.amount ? formatCurrency(c.value || c.amount) : 'N/A';
        const supplier = c.supplier || c.vendor || '-';
        report += `| ${contractTitle} | ${value} | ${supplier} |\n`;
      });
      report += `\n`;
    } else {
      report += `> ⚠️ **Note:** No similar historical contracts found. Competitor analysis based on market data only.\n\n`;
    }

    if (benchmark) {
      report += `### GSIN Benchmark (${phase4?.searchTerm || 'N/A'})\n\n`;
      report += `| Metric | Value |\n`;
      report += `|--------|-------|\n`;
      if (benchmark.avg_price) report += `| Average Price | ${formatCurrency(benchmark.avg_price)} |\n`;
      if (benchmark.median_price) report += `| Median Price | ${formatCurrency(benchmark.median_price)} |\n`;
      if (benchmark.min_price) report += `| Min Price | ${formatCurrency(benchmark.min_price)} |\n`;
      if (benchmark.max_price) report += `| Max Price | ${formatCurrency(benchmark.max_price)} |\n`;
      if (benchmark.contract_count) report += `| Contracts Analyzed | ${benchmark.contract_count} |\n`;
      report += `\n`;
    }

    // Competitor bands from Phase 5
    const phase5Products = phase5?.products || [];
    if (phase5Products.length > 0) {
      report += `### Competitor Price Bands\n\n`;
      report += `| Product | Floor | Market Low | Likely Cluster | Ceiling | Confidence |\n`;
      report += `|---------|-------|------------|----------------|---------|------------|\n`;
      phase5Products.forEach(p => {
        const cb = p.competitorBand || {};
        const floor = cb.floor ? formatCurrency(cb.floor) : '-';
        const low = cb.marketLow ? formatCurrency(cb.marketLow) : '-';
        const cluster = cb.likelyCluster ? formatCurrency(cb.likelyCluster) : '-';
        const ceiling = cb.ceiling ? formatCurrency(cb.ceiling) : '-';
        const conf = cb.confidence || '-';
        report += `| ${(p.name || '').substring(0, 20)} | ${floor} | ${low} | ${cluster} | ${ceiling} | ${conf} |\n`;
      });
      report += `\n`;
    }

    if (phase4?.buyerAnalysis?.analysis) {
      report += `**Buyer Intelligence:** ${phase4.buyerAnalysis.analysis.substring(0, 400)}...\n\n`;
    }

    report += `---\n\n`;

    // ==========================================
    // ANNEX B: CLIENT BID SHEET (Phase 5)
    // ==========================================
    report += `## Annex B: Client Bid Sheet\n\n`;

    const bidSummary = phase5?.totalBidSummary || {};

    report += `### Summary Pricing\n\n`;
    report += `| Metric | Win Strategy | Balanced Strategy |\n`;
    report += `|--------|--------------|-------------------|\n`;
    report += `| **Total Bid Price** | **${formatCurrency(bidSummary.totalWinPrice)}** | **${formatCurrency(bidSummary.totalBalancedPrice)}** |\n`;
    report += `| Margin | ${bidSummary.overallMarginWin || 'N/A'} | ${bidSummary.overallMarginBalanced || 'N/A'} |\n`;
    report += `| Total Landed Cost | ${formatCurrency(bidSummary.totalLandedCost)} | ${formatCurrency(bidSummary.totalLandedCost)} |\n`;
    report += `\n`;

    // Per-product bid sheet
    const bidProducts = phase5?.products || [];
    if (bidProducts.length > 0) {
      report += `### Per-Product Pricing\n\n`;
      report += `| Product | Landed Cost | Win Price | Win Margin | Balanced Price | Balanced Margin |\n`;
      report += `|---------|-------------|-----------|------------|----------------|------------------|\n`;
      bidProducts.forEach(p => {
        const landed = p.landedCostCAD ? formatCurrency(p.landedCostCAD) : '-';
        const winP = p.bidPrices?.winPrice ? formatCurrency(p.bidPrices.winPrice) : '-';
        const winM = p.bidPrices?.winMargin || '-';
        const balP = p.bidPrices?.balancedPrice ? formatCurrency(p.bidPrices.balancedPrice) : '-';
        const balM = p.bidPrices?.balancedMargin || '-';
        const noBidFlag = p.bidPrices?.noBid ? ' ❌' : '';
        report += `| ${(p.name || '').substring(0, 20)}${noBidFlag} | ${landed} | ${winP} | ${winM} | ${balP} | ${balM} |\n`;
      });
      report += `\n`;

      // No-bid items
      const noBidItems = bidProducts.filter(p => p.bidPrices?.noBid);
      if (noBidItems.length > 0) {
        report += `### Items Flagged as No-Bid\n\n`;
        noBidItems.forEach(p => {
          report += `- **${p.name}**: ${p.bidPrices?.noBidReason || 'Insufficient margin'}\n`;
        });
        report += `\n`;
      }
    }

    report += `---\n\n`;

    // ==========================================
    // FINAL SUMMARY & NEXT ACTIONS
    // ==========================================
    report += `## Final Summary\n\n`;

    const finalRec = bidSummary.bidRecommendation || 'REVIEW';
    const recText = finalRec === 'BID' ? '✅ PROCEED WITH BID' :
                   finalRec === 'CAUTION' ? '⚠️ PROCEED WITH CAUTION' : '❌ DO NOT BID';

    report += `### ${recText}\n\n`;

    if (finalRec === 'BID') {
      report += `This tender represents a viable business opportunity with acceptable margins and manageable risk.\n\n`;
      report += `**Recommended Bid:** ${formatCurrency(bidSummary.totalWinPrice)} (Win) or ${formatCurrency(bidSummary.totalBalancedPrice)} (Balanced)\n\n`;
    } else if (finalRec === 'CAUTION') {
      report += `This tender has marginal economics. Carefully evaluate strategic value before proceeding.\n\n`;
    } else {
      report += `Based on our analysis, the margin opportunity does not justify the effort and risk.\n\n`;
    }

    // Next Actions from Phase 5
    const nextActions = phase5?.nextActions || [];
    if (nextActions.length > 0) {
      report += `**Next Actions:**\n`;
      nextActions.forEach((action, idx) => {
        report += `${idx + 1}. ${action}\n`;
      });
      report += `\n`;
    } else {
      report += `**Next Actions:**\n`;
      report += `1. Complete compliance verification for all mandatory requirements\n`;
      report += `2. Confirm FOB pricing with OEM supplier\n`;
      report += `3. Validate shipping and delivery timelines\n`;
      report += `4. Prepare bid documentation per tender requirements\n`;
      report += `5. Submit bid by closing date\n\n`;
    }

    report += `---\n\n`;

    // ==========================================
    // DATA QUALITY SUMMARY
    // ==========================================
    report += `## Data Quality Summary\n\n`;
    report += `| Data Source | Status | Reliability |\n`;
    report += `|-------------|--------|-------------|\n`;
    report += `| PDF Documents | ${dataQuality.hasPdfContent ? '✅ Extracted' : '❌ Not Provided'} | ${dataQuality.hasPdfContent ? 'High' : 'Low'} |\n`;
    report += `| Products Extracted | ${dataQuality.extractedProducts || 0} items | ${dataQuality.extractedProducts > 0 ? 'Good' : 'Needs PDF'} |\n`;
    // Show HITL validation status if available
    if (dataQuality.humanValidated !== undefined) {
      report += `| Human Validation | ${dataQuality.humanApproved ? '✅ Approved' : '❌ Not Approved'} | ${dataQuality.humanApproved ? 'High' : 'Low'} |\n`;
    }
    report += `| Canada Pricing | ${dataQuality.hasCanadaPricing ? '✅ Found' : '⚠️ Not Found'} | ${dataQuality.hasCanadaPricing ? 'High' : 'Medium'} |\n`;
    report += `| China/OEM Pricing | ${dataQuality.hasChinaPricing ? '✅ Found' : '⚠️ Not Found'} | ${dataQuality.hasChinaPricing ? 'High' : 'Medium'} |\n`;
    report += `| Historical Contracts | ${dataQuality.hasHistoricalData ? `✅ ${phase4?.contractsFound || 0} found` : '⚠️ None found'} | ${dataQuality.hasHistoricalData ? 'High' : 'Low'} |\n`;
    report += `\n`;

    // Show human review feedback if available
    const humanReview = analysis?.humanReview;
    if (humanReview) {
      report += `### Human Review\n\n`;
      report += `- **Status:** ${humanReview.approved ? 'Approved' : 'Not Approved'}\n`;
      report += `- **Products Validated:** ${humanReview.productsValidated}\n`;
      if (humanReview.feedback) {
        report += `- **Reviewer Feedback:** ${humanReview.feedback}\n`;
      }
      report += `\n`;
    }

    if (!dataQuality.hasPdfContent) {
      report += `> ⚠️ **No PDF Documents Analyzed:** Upload RFP documents in Step 2 for accurate product extraction, compliance requirements, and pricing analysis.\n\n`;
    }

    if (!dataQuality.hasCanadaPricing || !dataQuality.hasChinaPricing) {
      report += `> **Note:** Some pricing data could not be retrieved. Before submitting a bid:\n`;
      report += `> 1. Verify pricing with actual supplier quotes\n`;
      report += `> 2. Research competitor pricing in this market segment\n`;
      report += `> 3. Confirm landed cost calculations with freight forwarder\n\n`;
    }

    report += `---\n\n`;
    report += `*Report generated by [AI]Quire Bid Factory - 5-Phase Sourcing Strategy*\n`;
    report += `*Generated: ${new Date().toLocaleString()}*\n`;

    return report;
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isSending) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);

    try {
      // Build context for the AI
      const prompt = `You are helping edit a bid analysis report. The user has requested the following change:

"${userMessage}"

Current document:
${reportContent}

Please provide the updated section(s) that address the user's request. Only include the parts that need to change, formatted in markdown.`;

      // Call the pricing intelligence ask endpoint for general AI queries
      const response = await pricingIntelligenceAPI.ask(prompt);

      let assistantMessage = '';
      if (response?.analysis) {
        assistantMessage = response.analysis;

        // If the response contains actual content changes, offer to apply them
        if (response.analysis.includes('#') || response.analysis.includes('**')) {
          assistantMessage += '\n\n*Would you like me to apply these changes to your document? Just say "apply changes" or I can make more adjustments.*';
        }
      } else {
        assistantMessage = "I understand your request. Let me suggest some changes:\n\n" +
          "Could you be more specific about which section you'd like me to modify? For example:\n" +
          "- Executive Summary\n- Compliance Matrix\n- Pricing Tables\n- Bid Sheet";
      }

      setChatMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);

      // Check if user wants to apply changes
      if (userMessage.toLowerCase().includes('apply') || userMessage.toLowerCase().includes('update')) {
        // Auto-regenerate with context
        handleRegenerate();
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I encountered an error processing your request. Please try again or rephrase your request."
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleRegenerate = async () => {
    // Re-run the full AI analysis
    if (onRerunAnalysis) {
      onRerunAnalysis();
    } else {
      // Fallback: just rebuild report from existing data
      setIsGenerating(true);
      try {
        const report = buildAnalystReport(tender, analysisResults, analystConfig);
        setReportContent(report);
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: "I've regenerated the report with the latest analysis data. Let me know if you'd like any further adjustments!"
        }]);
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reportContent);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const handleExport = () => {
    const blob = new Blob([reportContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bid-analysis-${tender?.tender_id || tender?.id || 'draft'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleComplete = () => {
    saveReport();
    onComplete({ reportContent, chatMessages, status: 'complete' });
  };

  // Get bid recommendation badge
  const getBidBadge = () => {
    const bidNoBid = analysisResults?.phase5?.bidNoBid || 'REVIEW';
    if (bidNoBid === 'BID') {
      return <span className="bid-badge bid">BID</span>;
    } else if (bidNoBid === 'CAUTION') {
      return <span className="bid-badge caution">CAUTION</span>;
    } else {
      return <span className="bid-badge no-bid">NO BID</span>;
    }
  };

  return (
    <div className="response-writing-v2">
      {/* Header */}
      <div className="writing-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={16} />
            Back
          </button>
          <div className="header-info">
            <h3>
              Analyst Bid Report
              {getBidBadge()}
            </h3>
            <p>{tender?.title || 'Tender Response'}</p>
          </div>
        </div>

        <div className="header-actions">
          {lastSaved && (
            <span className="save-status">
              <CheckCircle size={14} />
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <button className="btn-icon" onClick={handleRegenerate} disabled={isGenerating} title="Regenerate">
            <RefreshCw size={16} className={isGenerating ? 'spinning' : ''} />
          </button>
          <button className="btn-icon" onClick={handleCopy} title="Copy to clipboard">
            {showCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
          </button>
          <button className="btn-secondary" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary" onClick={handleComplete}>
            <CheckCircle size={16} />
            Save & Complete
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="writing-content">
        {/* Report Panel */}
        <div className="report-panel">
          {isGenerating ? (
            <div className="report-generating">
              <Sparkles size={32} className="spinning" />
              <p>Generating your analyst bid report...</p>
            </div>
          ) : (
            <div className="report-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{reportContent}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Chat Panel */}
        <div className="chat-panel">
          <div className="chat-header">
            <MessageSquare size={18} />
            <h4>Edit with AI</h4>
          </div>

          <div className="chat-messages">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="message-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              </div>
            ))}
            {isSending && (
              <div className="chat-message assistant">
                <div className="message-content typing">
                  <Loader size={16} className="spinning" />
                  <span>Thinking...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input-container">
            <textarea
              ref={textareaRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me to make changes to your report..."
              rows={2}
              disabled={isSending}
            />
            <button
              className="send-btn"
              onClick={handleSendMessage}
              disabled={!chatInput.trim() || isSending}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseWriting;
